'use client'

import { createSlice } from '@reduxjs/toolkit'
import { optionsList } from '../constants/user-role-constants'

// Initial state for the userRole slice
const INITIAL_STATE = {
  loading: false, // Indicates if an operation (like fetching) is in progress
  filters: {}, // Stores temporary filter values (e.g., selected values in dropdowns)
  optionsList: optionsList, // Default list of dropdown/filter options from constants
  filtersApplied: {}, // Stores filters that are actually applied (e.g., on API call)
  usersList: [], // List of users returned from backend/API
  totalCount: 0, // Total count of users (used for pagination, etc.)
}

// Creating the Redux slice for user role related state
const userRoleSlice = createSlice({
  name: 'userRole',
  initialState: INITIAL_STATE,
  reducers: {
    // Sets or updates a single filter value
    setFilters(state, action) {
      const { key, val } = action.payload
      state.filters = {
        ...state.filters,
        [key]: val,
      }
    },

    // Updates the optionsList with new values (e.g., after an API call)
    setOptionsList(state, action) {
      state.optionsList = { ...state.optionsList, ...action.payload }
    },

    // Applies all the current filters and stores them in filtersApplied
    setAllFilterApplied(state, action) {
      state.filtersApplied = action.payload
    },

    // Clears a specific filter from both filters and filtersApplied
    clearFilters(state, action) {
      const filters: any = state.filtersApplied
      delete filters[action.payload] // Remove the filter by key
      state.filters = filters
      state.filtersApplied = filters
    },

    // Clears all filters (used when clicking "Clear All Filters" in UI)
    clearAllFilters(state) {
      state.filters = {}
      state.filtersApplied = {}
    },

    // Sets the users list (typically after API call based on filters)
    setUsersList(state, action) {
      state.usersList = action.payload
    },

    // Sets the total count of users (usually used for pagination)
    setTotalCount(state, action) {
      state.totalCount = action.payload
    },

    // Sets the loading status (used for showing loaders/spinners)
    setLoading(state, action) {
      state.loading = action.payload
    },
  },
})

// Exporting all action creators generated by createSlice
export const {
  setFilters,
  setOptionsList,
  setAllFilterApplied,
  clearAllFilters,
  clearFilters,
  setUsersList,
  setTotalCount,
  setLoading,
} = userRoleSlice.actions

// Exporting the reducer to be used in the store
export default userRoleSlice
